import { Outlet } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import AskSifuChatbot from "../components/AskSifuChatbot";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useSubscriptionPolling } from "../hooks/useSubscriptionPolling";
import {
  useBillingSetupRedirect,
  usePostCheckoutUpgrade,
} from "../hooks/useBillingSetupRedirect";

const AppLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Automatically poll subscription status every 2 minutes
  useSubscriptionPolling();
  usePostCheckoutUpgrade();
  useBillingSetupRedirect();
  const hideSidebarRoutes = [
    "/app/onboarding",
    "/app/finishing-setup",
    "/app/upgrade-complete",
    "/app/student-sign-in",
  ];
  const hideSidebar = hideSidebarRoutes.includes(location.pathname);
  const hideChatbot =
    hideSidebar ||
    location.pathname === "/app/kiosk-mode" ||
    location.pathname.startsWith("/app/inactive-studio");
  const noPaddingRoutes = [
    "/app/onboarding",
    "/app/finishing-setup",
    "/app/student-sign-in",
    "/app/inactive-studio",
  ];
  const noPadding = noPaddingRoutes.some(route => location.pathname.startsWith(route));

  // Lock-in logic for student sign-in after refresh
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      sessionStorage.getItem("lockStudentSignIn") === "true" &&
      location.pathname !== "/app/student-sign-in"
    ) {
      navigate("/app/student-sign-in", { replace: true });
    }
  }, [location.pathname, navigate]);

  // Update page title based on current route
  useEffect(() => {
    const pageTitles: Record<string, string> = {
      "/app": "Dashboard | DojoTrack",
      "/app/dashboard": "Dashboard | DojoTrack",
      "/app/students": "Student Management | DojoTrack",
      "/app/leads": "Lead Management | DojoTrack",
      "/app/staff": "Staff Management | DojoTrack",
      "/app/classes-events": "Classes & Events | DojoTrack",
      "/app/events": "Events | DojoTrack",
      "/app/attendance": "Attendance | DojoTrack",
      "/app/messaging": "Email Messaging | DojoTrack",
      "/app/sms": "SMS Messaging | DojoTrack",
      "/app/sms/history": "SMS History | DojoTrack",
      "/app/push-notifications": "Push Notifications | DojoTrack",
      "/app/forms": "Website Forms | DojoTrack",
      "/app/waivers": "Waivers | DojoTrack",
      "/app/store": "Store | DojoTrack",
      "/app/student-signin": "Student Sign In | DojoTrack",
      "/app/student-sign-in": "Student Sign In | DojoTrack",
      "/app/kiosk-mode": "Kiosk Mode | DojoTrack",
      "/app/updates": "Updates | DojoTrack",
      "/app/report-bug": "Report a Bug | DojoTrack",
      "/app/settings": "Settings | DojoTrack",
      "/app/settings/profile": "Profile Settings | DojoTrack",
      "/app/settings/general": "General Settings | DojoTrack",
      "/app/settings/billing": "Billing Settings | DojoTrack",
      "/app/settings/security": "Security Settings | DojoTrack",
      "/app/settings/integrations": "Integrations | DojoTrack",
      "/app/settings/sync": "Sync Settings | DojoTrack",
      "/app/settings/family-discounts": "Family Discounts & ID's | DojoTrack",
      "/app/settings/discount-codes": "Discount Codes | DojoTrack",
      "/app/onboarding": "Welcome to DojoTrack | DojoTrack",
      "/app/finishing-setup": "Finishing Setup | DojoTrack",
      "/app/inactive-studio/settings/billing": "Reactivate Your Account | DojoTrack",
    };

    const title = pageTitles[location.pathname] || "DojoTrack";
    document.title = title;
  }, [location.pathname]);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100">
      {!hideSidebar && <Sidebar />}
      <div className="flex-1 flex flex-col h-screen overflow-y-auto">
        <main
          className={`bg-gray-50 dark:bg-gray-900 flex-1${
            noPadding ? "" : " py-8 px-4 xl:p-8"
          }`}
        >
          <Outlet />
        </main>
      </div>
      <AskSifuChatbot hidden={hideChatbot} />
    </div>
  );
};

export default AppLayout;
